import React, { useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, X, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * FileUploader — uploads to /api/files and emits attached file objects.
 * Props:
 *  - folder: 'services' | 'portfolio' | 'deliveries' | 'messages' | 'avatars'
 *  - value: array of file objects [{file_id, url, original_filename, content_type, size}]
 *  - onChange(next)
 *  - accept (HTML accept attribute, default any)
 *  - max (max files, default 8)
 *  - label (button label)
 *  - imageMode (boolean) — render image previews
 *  - testId
 */
export default function FileUploader({
  folder = "uploads",
  value = [],
  onChange,
  accept,
  max = 8,
  label = "Attach files",
  imageMode = false,
  testId,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Max ${max} files`);
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    setBusy(true);
    const out = [...value];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder);
      try {
        const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    onChange?.(out);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (i) => onChange?.(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" variant="outline" disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testId || "uploader"}-trigger`}>
          {busy ? "Uploading…" : <><UploadSimple size={16} className="mr-1.5" weight="bold" /> {label}</>}
        </Button>
        <input ref={inputRef} type="file" multiple accept={accept || undefined} className="hidden"
          onChange={(e) => handleFiles(e.target.files || [])} />
        {value.length > 0 && <span className="text-xs text-gray-500">{value.length}/{max} files</span>}
      </div>

      {imageMode ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {value.map((f, i) => (
            <div key={f.file_id} className="relative rounded-lg overflow-hidden border border-gray-200 aspect-[4/3]">
              <img src={`${API}/files/${f.file_id}`} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 flex items-center justify-center">
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        value.length > 0 && (
          <ul className="space-y-1">
            {value.map((f, i) => (
              <li key={f.file_id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-200">
                <a href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer"
                   className="text-sm font-medium text-mv-secondary hover:text-mv-primary truncate flex items-center gap-2 max-w-[80%]">
                  <Paperclip size={14} weight="duotone" /> {f.original_filename}
                </a>
                <button type="button" onClick={() => remove(i)} className="text-gray-400 hover:text-rose-500">
                  <X size={14} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
